import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  getAiSpendReport, PIVOT_DIMENSIONS, PIVOT_LABELS,
  sumPivot, withMargin,
  type MarginRow, type PivotDimension,
} from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';
import TenantSpendDrilldown from './TenantSpendDrilldown';
import UsagePivotTable from './UsagePivotTable';

const usd = (n: number): string => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;

/**
 * The pivotable VENDOR SPEND report (spec §6): the same spend by tenant,
 * service, feature, role or model, each row showing calls, tokens and true
 * provider cost. Super-admin only (spec §9.2).
 *
 * No charged / margin columns (final review #3): Ruby engine spend rows record
 * creditsCharged 0 — the charge lives on the RUN row — so margin built from
 * these rows would read every run as a total loss. Charged and margin are the
 * Ruby Ledger's (Ledger tab, Overview).
 *
 * All arithmetic lives in aiControl/pivot.ts; this component only renders.
 */
const UsageTab: React.FC<AiTabProps> = ({ range, tenantId, onHeaderStats }) => {
  const [dimension, setDimension] = useState<PivotDimension>('tenant');
  const [rows, setRows] = useState<MarginRow[]>([]);
  const [creditUnitPriceUsd, setCreditUnitPriceUsd] = useState(0.01);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [drill, setDrill] = useState<MarginRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const report = await getAiSpendReport(dimension, range);
        if (cancelled) return;
        setCreditUnitPriceUsd(report.creditUnitPriceUsd);
        setRows(withMargin(report.rows, report.creditUnitPriceUsd));
      } catch (err: any) {
        // The endpoint is Plan 4's and may not be deployed: a typed error plus
        // an empty table, never a white screen.
        if (!cancelled) { setError(err?.message || 'Failed to load the spend report'); setRows([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [dimension, range.fromIso, range.toIso]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (sortDir === 'asc' ? a.costUsd - b.costUsd : b.costUsd - a.costUsd)),
    [rows, sortDir],
  );
  const totals = useMemo(() => sumPivot(rows), [rows]);

  useEffect(() => {
    onHeaderStats([
      { label: 'Vendor cost', value: usd(totals.costUsd), tone: 'rose' },
      { label: 'Calls', value: totals.requests.toLocaleString(), tone: 'white' },
    ]);
  }, [totals, onHeaderStats]);

  // A ?tenantId= deep link (from the retired /ai-status?tenantId=) opens that
  // tenant's drill-down as soon as the tenant pivot has loaded.
  useEffect(() => {
    if (!tenantId || drill || dimension !== 'tenant') return;
    const hit = rows.find((r) => r.key === tenantId);
    if (hit) setDrill(hit);
  }, [tenantId, rows, dimension, drill]);

  return (
    <>
      <div className="bg-white border border-stone-200 rounded-xl shadow-sm px-4 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Vendor spend</h2>
            <p className="text-[11px] text-gray-500">
              Every recorded call and what the vendor billed us for it. What tenants were
              charged, and the margin, are on the Ruby Ledger tab.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {PIVOT_DIMENSIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDimension(d)}
                className={`px-3 py-2 min-h-[44px] rounded-lg text-xs font-semibold border transition-colors ${
                  dimension === d
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'
                }`}
              >
                {PIVOT_LABELS[d]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Failed to load AI spend</p>
            <p className="text-rose-600 text-xs mt-0.5">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
        <UsagePivotTable
          dimension={dimension}
          rows={sorted}
          totals={totals}
          loading={loading}
          sortDir={sortDir}
          onSortToggle={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
          onDrill={setDrill}
        />
      </div>

      {drill && (
        <TenantSpendDrilldown
          row={drill}
          dimension={dimension}
          range={range}
          creditUnitPriceUsd={creditUnitPriceUsd}
          onClose={() => setDrill(null)}
        />
      )}
    </>
  );
};

export default UsageTab;
